/**
 * useBuffetRules — per-product buffet rules acceptance gate (table/QR flow).
 *
 * Scope: applies ONLY to products flagged requires_buffet_rules (e.g. the
 * Rodízio buffet). Browsing the menu and ordering normal products is never
 * affected; delivery, takeaway and the conventional menu never see the modal.
 *
 * Source of truth: the canonical table_sessions document
 * (buffet_rules_accepted/_at), persisted via
 * POST /public/table-session/{id}/accept-buffet-rules and returned by the
 * session GET. A per-tab sessionStorage flag is only a fallback when no
 * canonical session exists. A new table session re-arms the modal.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";

const sessionKey = (sessionId) => `kaiso_buffet_rules_${sessionId}`;
const tableKey = (tableId) => `kaiso_buffet_rules_table_${tableId}`;

export const requiresBuffetRules = (product) => product?.requires_buffet_rules === true;

function readLocalAcceptance(sessionId, tableId) {
  try {
    if (sessionId) return sessionStorage.getItem(sessionKey(sessionId)) === "true";
    if (tableId) return sessionStorage.getItem(tableKey(tableId)) === "true";
  } catch {
    // sessionStorage unavailable (private mode) — rely on backend flag only
  }
  return false;
}

export function useBuffetRules({ isTableOrder, tableSession, setTableSession, tableId, API, cart, landingProduct }) {
  const [showBuffetRulesModal, setShowBuffetRulesModal] = useState(false);
  const [acceptingRules, setAcceptingRules] = useState(false);
  const pendingActionRef = useRef(null);
  const landingPromptedForRef = useRef(null);

  const active = isTableOrder === true;
  const sessionId = tableSession?.session_id || null;
  const rulesAccepted =
    tableSession?.buffet_rules_accepted === true || readLocalAcceptance(sessionId, tableId);
  const gateOpen = active && !rulesAccepted;

  // Direct product link (landing mode) to a flagged product: show the rules
  // before the customer can start the order. Once per session+product.
  useEffect(() => {
    if (!gateOpen || !requiresBuffetRules(landingProduct)) return;
    const key = `${sessionId || tableId || "-"}:${landingProduct.id}`;
    if (landingPromptedForRef.current === key) return;
    landingPromptedForRef.current = key;
    setShowBuffetRulesModal(true);
  }, [gateOpen, landingProduct, sessionId, tableId]);

  const closeBuffetRulesModal = () => {
    pendingActionRef.current = null;
    setShowBuffetRulesModal(false);
  };

  const acceptBuffetRules = async () => {
    setAcceptingRules(true);
    if (sessionId) {
      try {
        const res = await axios.post(`${API}/public/table-session/${sessionId}/accept-buffet-rules`);
        const updated = {
          ...tableSession,
          buffet_rules_accepted: true,
          buffet_rules_accepted_at: res.data?.buffet_rules_accepted_at || new Date().toISOString(),
        };
        setTableSession?.(updated);
        if (tableId) {
          try { localStorage.setItem(`kaiso_table_session_${tableId}`, JSON.stringify(updated)); } catch {}
        }
      } catch (err) {
        // Older backend or transient failure — never block the critical order
        // flow on this side-effect; keep a per-tab acceptance and log it.
        console.warn("[BUFFET_RULES] backend accept failed, using local fallback:", err?.response?.status || err?.message);
      }
      try { sessionStorage.setItem(sessionKey(sessionId), "true"); } catch {}
    } else if (tableId) {
      try { sessionStorage.setItem(tableKey(tableId), "true"); } catch {}
    }
    setAcceptingRules(false);
    setShowBuffetRulesModal(false);
    const pending = pendingActionRef.current;
    pendingActionRef.current = null;
    if (pending) pending();
  };

  // Guard actions whose FIRST argument is the product being added/started
  // (addToCart, addToCartSilent, addSuggestionToCart, landing add). Only a
  // flagged product opens the modal; everything else passes straight through.
  const guardProductAction = (fn) => (...args) => {
    if (gateOpen && requiresBuffetRules(args[0])) {
      pendingActionRef.current = () => fn?.(...args);
      setShowBuffetRulesModal(true);
      return undefined;
    }
    return fn?.(...args);
  };

  // Guard cart-level actions (open checkout / submit) only when the cart
  // already holds a flagged product without acceptance — defense in depth.
  const guardCartAction = (fn) => (...args) => {
    if (gateOpen && (cart || []).some(requiresBuffetRules)) {
      setShowBuffetRulesModal(true);
      return undefined;
    }
    return fn?.(...args);
  };

  return {
    buffetRulesActive: active,
    rulesAccepted,
    showBuffetRulesModal,
    setShowBuffetRulesModal,
    closeBuffetRulesModal,
    acceptingRules,
    acceptBuffetRules,
    guardProductAction,
    guardCartAction,
  };
}
